import {
  type Camera,
  Raycaster,
  type SkinnedMesh,
  Vector2,
  Vector3
} from 'three'
import { escape_html } from '../../lib/html.ts'

/**
 * Click-to-inspect tool for skinned mesh weights. When active, intercepts
 * canvas clicks, raycasts against the live skinned mesh, finds the nearest
 * vertex of the hit triangle, and reports which bones influence it.
 *
 * Lives outside the EditSkeleton/Animations steps so it can be used at any
 * point after the mesh is bound. Renders into a small floating panel that
 * appears on first activation and is hidden when the inspector is disabled.
 *
 * Implementation note: the inspector reads `skinIndex` / `skinWeight` buffer
 * attributes directly — it does NOT recompute weights. The values shown are
 * exactly what the GPU uses to deform the mesh, which is the most useful
 * thing for debugging skinning issues.
 */

export interface VertexInfluence {
  bone_index: number
  bone_name: string
  weight: number
}

export interface VertexInspectionResult {
  vertex_index: number
  world_position: Vector3
  influences: VertexInfluence[]
}

export interface VertexInfluenceInspectorOptions {
  canvas: HTMLCanvasElement
  camera: Camera
  /** Returns the currently bound skinned meshes. Returning empty disables
   *  inspection cleanly. */
  get_meshes: () => SkinnedMesh[]
}

export class VertexInfluenceInspector {
  private active: boolean = false
  private readonly raycaster: Raycaster = new Raycaster()
  private readonly mouse: Vector2 = new Vector2()
  private readonly mousedown_handler: (e: MouseEvent) => void
  private panel: HTMLDivElement | undefined

  constructor (private readonly options: VertexInfluenceInspectorOptions) {
    this.mousedown_handler = (e) => this.handle_mousedown(e)
  }

  public is_active (): boolean { return this.active }

  public set_active (active: boolean): void {
    if (this.active === active) return
    this.active = active
    if (active) {
      this.options.canvas.addEventListener('mousedown', this.mousedown_handler, true)
      this.show_panel('Click a vertex on the mesh to inspect its bone weights.')
      this.options.canvas.style.cursor = 'crosshair'
    } else {
      this.options.canvas.removeEventListener('mousedown', this.mousedown_handler, true)
      this.hide_panel()
      this.options.canvas.style.cursor = ''
    }
  }

  /**
   * Run a single inspection at the given canvas coordinates. Returns the
   * inspection result or null if nothing was hit. Exposed so callers can
   * trigger inspections programmatically (tests, keyboard shortcuts, etc.)
   * without going through the canvas mousedown path.
   */
  public inspect_at (client_x: number, client_y: number): VertexInspectionResult | null {
    const meshes = this.options.get_meshes()
    if (meshes.length === 0) return null
    const rect = this.options.canvas.getBoundingClientRect()
    this.mouse.x = ((client_x - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((client_y - rect.top) / rect.height) * 2 + 1
    this.raycaster.setFromCamera(this.mouse, this.options.camera)
    const hits = this.raycaster.intersectObjects(meshes, false)
    if (hits.length === 0) return null
    const hit = hits[0]
    const mesh = hit.object as SkinnedMesh
    const face = hit.face
    if (face === null || face === undefined) return null

    const geom = mesh.geometry
    const pos = geom.attributes.position
    const skin_index = geom.attributes.skinIndex
    const skin_weight = geom.attributes.skinWeight
    if (pos === undefined || skin_index === undefined || skin_weight === undefined) return null

    // Find the closest vertex of the hit triangle to the actual hit point.
    let best_vi = face.a
    let best_dsq = Infinity
    for (const vi of [face.a, face.b, face.c]) {
      const v = new Vector3().fromBufferAttribute(pos, vi).applyMatrix4(mesh.matrixWorld)
      const dsq = v.distanceToSquared(hit.point)
      if (dsq < best_dsq) { best_dsq = dsq; best_vi = vi }
    }

    // skinIndex/skinWeight are itemSize=4 (up to 4 bones per vertex).
    const influences: VertexInfluence[] = []
    const bones = mesh.skeleton.bones
    for (let k = 0; k < 4; k++) {
      const bi = skin_index.getComponent(best_vi, k)
      const w = skin_weight.getComponent(best_vi, k)
      if (w <= 0) continue
      const bone = bones[bi]
      influences.push({
        bone_index: bi,
        bone_name: bone?.name ?? `(bone ${bi})`,
        weight: w
      })
    }
    influences.sort((a, b) => b.weight - a.weight)

    const world_position = new Vector3().fromBufferAttribute(pos, best_vi).applyMatrix4(mesh.matrixWorld)
    return { vertex_index: best_vi, world_position, influences }
  }

  private handle_mousedown (e: MouseEvent): void {
    if (e.button !== 0) return
    const result = this.inspect_at(e.clientX, e.clientY)
    if (result === null) {
      this.show_panel('No vertex hit — try clicking directly on the mesh.')
      return
    }
    e.stopPropagation()
    this.render_result(result)
  }

  private show_panel (message: string): void {
    this.ensure_panel()
    if (this.panel === undefined) return
    this.panel.style.display = 'block'
    this.panel.innerHTML = `<div style="opacity: 0.85;">${escape_html(message)}</div>`
  }

  private hide_panel (): void {
    if (this.panel !== undefined) this.panel.style.display = 'none'
  }

  private render_result (result: VertexInspectionResult): void {
    this.ensure_panel()
    if (this.panel === undefined) return
    this.panel.style.display = 'block'
    const rows = result.influences.map(inf =>
      `<tr><td style="padding-right: 0.75rem;">${escape_html(inf.bone_name)}</td><td style="text-align: right; font-variant-numeric: tabular-nums;">${(inf.weight * 100).toFixed(1)}%</td></tr>`
    ).join('')
    this.panel.innerHTML = `
      <div style="font-weight: 600; margin-bottom: 0.25rem;">Vertex #${result.vertex_index}</div>
      <div style="font-size: 0.8em; opacity: 0.75; margin-bottom: 0.5rem;">
        @ (${result.world_position.x.toFixed(3)}, ${result.world_position.y.toFixed(3)}, ${result.world_position.z.toFixed(3)})
      </div>
      <table style="border-collapse: collapse;">${rows}</table>
    `
  }

  private ensure_panel (): void {
    if (this.panel !== undefined) return
    const el = document.createElement('div')
    el.id = 'vertex-influence-inspector-panel'
    Object.assign(el.style, {
      position: 'fixed',
      top: '70px',
      right: '12px',
      maxWidth: '260px',
      background: 'rgba(20,20,20,0.92)',
      color: '#eee',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: '4px',
      padding: '0.5rem 0.75rem',
      font: '12px/1.4 system-ui, sans-serif',
      zIndex: '1000',
      pointerEvents: 'none'
    })
    document.body.appendChild(el)
    this.panel = el
  }
}
